import { DropdownButton } from '@/components/DropdownButton'
import { Button } from '@/components/ui/button'
import { useTargetedAreaWebsocket } from '@/hooks/useTargetedAreaWebsocket'
import { useAuth } from '@/loaders/Auth'
import { versionSelector } from '@/redux/store/slices/grid/gridSlice'
import { selectedReferenceFieldSelector } from '@/redux/store/slices/map/mapSlice'
import { setIsSyncToGoogleDialogOpen } from '@/redux/store/slices/ui/uiSlice'
import { useAppDispatch, useAppSelector } from '@redux/hooks/useAppState'
import { useStytchOrganization } from '@stytch/react/b2b'
import { ThirdPartyPlatforms } from 'honeygrid-types'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { StatsTable } from '../StatsTable/StatsTable'
import { useExportTargeting } from './hooks/useExportTargeting'
import { ReferenceFieldSelect } from './ReferenceFieldSelect'

import type { FC } from 'react'

interface MapControlsProps {
	targetingSelection?: string[]
}

const options = [
	{
		label: 'Push to Google Ads',
		value: ThirdPartyPlatforms.GoogleSync,
	},
	{
		label: 'Export Google Ads CSV',
		value: ThirdPartyPlatforms.GoogleCsv,
	},
	{
		label: 'Targeted cells',
		value: ThirdPartyPlatforms.All,
	},
]

export const MapControls: FC<MapControlsProps> = () => {
	const [statsTableOpen, setStatsTableOpen] = useState(false)
	const navigate = useNavigate()
	const { workspaceId } = useParams()
	const { organization } = useStytchOrganization()
	const { data: honeygridData } = useTargetedAreaWebsocket()
	const tableReferenceField = useAppSelector(selectedReferenceFieldSelector)
	const dispatch = useAppDispatch()
	const { loggedIn } = useAuth()
	const draftVersion = useAppSelector(versionSelector)
	const { generateTargetingList, exportTargeting } = useExportTargeting()

	// Extract custom field keys and their friendly names from the grid data
	const customFieldKeys = honeygridData?.fieldMappings
		? Object.keys(honeygridData.fieldMappings)
		: []
	const customFieldMappings = honeygridData?.fieldMappings || {}

	return (
		<>
			<ReferenceFieldSelect customDataKeys={customFieldKeys} fieldMappings={customFieldMappings} />
			<div className="z-50 rounded-none p-2 border-none">
				<div className="flex justify-between">
					<div className="flex gap-2 m-1 w-full">
						<Button
							variant="outline"
							className="cursor-pointer"
							onClick={() => setStatsTableOpen((current) => !current)}
						>
							{statsTableOpen ? <ChevronDown /> : <ChevronUp />}
							{statsTableOpen ? 'Hide' : 'Show'} data table
						</Button>
						<Button
							variant="outline"
							className="cursor-pointer"
							onClick={() => navigate(`/${workspaceId}/data-sources/csv`)}
						>
							Connect data
						</Button>
						{loggedIn && (
							<DropdownButton
								options={options}
								label="Export targeting"
								buttonProps={{
									variant: 'outline',
								}}
								renderOption={(option) => option.label}
								onOptionSelected={(platform) => {
									if (platform.value === ThirdPartyPlatforms.GoogleSync) {
										dispatch(setIsSyncToGoogleDialogOpen(true))
									} else {
										exportTargeting(
											generateTargetingList(draftVersion, honeygridData?.aggregatedDemographics),
											platform.value,
											organization?.organization_id
										)
									}
								}}
							/>
						)}
					</div>
				</div>
				<StatsTable
					open={statsTableOpen}
					aggregatedDemographics={honeygridData?.aggregatedDemographics}
					tableReferenceField={tableReferenceField}
					handleClose={() => setStatsTableOpen(false)}
					customKeys={customFieldKeys}
					fieldMappings={customFieldMappings}
				/>
			</div>
		</>
	)
}
